import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Explosion } from "./karma/explosion";
import { KarmaText } from "./karma/KarmaText";
import { Participants } from "./karma/Participants";
import KarmaTitle from "./karma/Title";
import KarmaFooter from "./karma/KarmaFooter";
import { useKarma } from "../hooks/useKarma";

interface BombOverlayProps {
  isActive: boolean;
  duration?: number;
  onComplete?: () => void;
  soundEnabled?: boolean;
  winners?: string[];
  prize?: number;
}

export default function Karma() {
  const {
    isActive,
    participants,
    winners,
    prize,
    showBombOverlay,
    resetKarma,
  } = useKarma();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const soundPlayedRef = useRef(false);

  // Reset time when karma starts
  useEffect(() => {
    if (isActive && !showBombOverlay) {
      setTimeRemaining(60);
    }
  }, [isActive, showBombOverlay]);

  function playSound() {
    const audio = new Audio("/reveal3.mp3");
    audio.volume = 0.2;
    audio.play().catch((err) => {
      console.error("Sound playback failed:", err);
      soundPlayedRef.current = false; // Reset on error
    });
  }

  // Countdown timer - only when karma is active
  useEffect(() => {
    if (!isActive || showBombOverlay) return;
    playSound();

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isActive, showBombOverlay]);

  const isVisible = isActive;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="absolute inset-0 flex flex-col p-4 z-50 rounded-3xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(20, 10, 40, 0.98) 0%, rgba(40, 20, 60, 0.98) 50%, rgba(20, 10, 40, 0.98) 100%)",

            backdropFilter: "blur(8px)",
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.5 } }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {!showBombOverlay && (
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <KarmaTitle timeRemaining={timeRemaining} />
              <Participants participants={participants} />
              <KarmaFooter />
            </motion.div>
          )}
          <BombOverlay
            isActive={showBombOverlay}
            winners={winners}
            prize={prize}
            onComplete={resetKarma}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const BombOverlay = ({
  isActive,
  duration = 3000,
  onComplete,
  soundEnabled = true,
  winners = [],
  prize = 1000,
}: BombOverlayProps) => {
  const [progress, setProgress] = useState(0);
  const [showExplosion, setShowExplosion] = useState(false);
  const [showKarmaText, setShowKarmaText] = useState(false);
  const [showWinners, setShowWinners] = useState(false);
  const [fusePosition, setFusePosition] = useState({ x: 0, y: 0 });
  const width = 462;
  const height = 490;
  const strokeWidth = 8;
  const padding = 1;
  const borderRadius = 20;
  const perimeter =
    2 * (width + height) - 8 * borderRadius + 2 * Math.PI * borderRadius;

  const calculateFusePosition = useCallback(
    (prog: number) => {
      const straightTop = width - 2 * borderRadius;
      const straightRight = height - 2 * borderRadius;
      const straightBottom = width - 2 * borderRadius;
      const straightLeft = height - 2 * borderRadius;
      const cornerLength = (Math.PI * borderRadius) / 2;

      const totalLength =
        straightTop +
        cornerLength +
        straightRight +
        cornerLength +
        straightBottom +
        cornerLength +
        straightLeft +
        cornerLength;

      let currentLength = prog * totalLength;

      // Top edge
      if (currentLength <= straightTop) {
        return { x: padding + borderRadius + currentLength, y: padding };
      }
      currentLength -= straightTop;

      // Top-right corner
      if (currentLength <= cornerLength) {
        const angle = (currentLength / cornerLength) * (Math.PI / 2);
        return {
          x: padding + width - borderRadius + borderRadius * Math.sin(angle),
          y: padding + borderRadius - borderRadius * Math.cos(angle),
        };
      }
      currentLength -= cornerLength;

      // Right edge
      if (currentLength <= straightRight) {
        return {
          x: padding + width,
          y: padding + borderRadius + currentLength,
        };
      }
      currentLength -= straightRight;

      // Bottom-right corner
      if (currentLength <= cornerLength) {
        const angle = (currentLength / cornerLength) * (Math.PI / 2);
        return {
          x: padding + width - borderRadius + borderRadius * Math.cos(angle),
          y: padding + height - borderRadius + borderRadius * Math.sin(angle),
        };
      }
      currentLength -= cornerLength;

      // Bottom edge
      if (currentLength <= straightBottom) {
        return {
          x: padding + width - borderRadius - currentLength,
          y: padding + height,
        };
      }
      currentLength -= straightBottom;

      // Bottom-left corner
      if (currentLength <= cornerLength) {
        const angle = (currentLength / cornerLength) * (Math.PI / 2);
        return {
          x: padding + borderRadius - borderRadius * Math.sin(angle),
          y: padding + height - borderRadius + borderRadius * Math.cos(angle),
        };
      }
      currentLength -= cornerLength;

      // Left edge
      if (currentLength <= straightLeft) {
        return {
          x: padding,
          y: padding + height - borderRadius - currentLength,
        };
      }
      currentLength -= straightLeft;

      // Top-left corner
      const angle = (currentLength / cornerLength) * (Math.PI / 2);
      return {
        x: padding + borderRadius - borderRadius * Math.cos(angle),
        y: padding + borderRadius - borderRadius * Math.sin(angle),
      };
    },
    [width, height, padding, borderRadius],
  );

  useEffect(() => {
    if (!isActive) {
      setProgress(0);
      setShowExplosion(false);
      setShowKarmaText(false);
      setShowWinners(false);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(elapsed / duration, 1);
      setProgress(newProgress);
      setFusePosition(calculateFusePosition(newProgress));

      if (newProgress >= 1) {
        clearInterval(interval);
        setShowExplosion(true);
        if (soundEnabled) {
          //   playExplosionSound();
        }
        setTimeout(() => {
          setShowKarmaText(true);
        }, 800);
        setTimeout(() => {
          setShowWinners(true);
        }, 3000);
        setTimeout(() => {
          onComplete?.();
        }, 10000);
      }
    }, 16);

    return () => clearInterval(interval);
  }, [
    isActive,
    duration,
    onComplete,
    calculateFusePosition,
    soundEnabled,
    // playExplosionSound,
  ]);

  if (!isActive && !showExplosion) return null;

  const timeRemaining = Math.ceil((1 - progress) * (duration / 1000));

  return (
    <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none overflow-visible">
      <div
        className="relative rounded-2xl"
        style={{ width: width + padding * 2, height: height + padding * 2 }}
      >
        <svg
          width={width + padding * 2}
          height={height + padding * 2}
          className="absolute inset-0 "
        >
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Unburned fuse */}
          <rect
            x={padding}
            y={padding}
            width={width}
            height={height}
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth={strokeWidth}
            rx={borderRadius}
            pathLength={perimeter}
          />

          {/* Burned trail */}
          <rect
            x={padding}
            y={padding}
            width={width}
            height={height}
            fill="none"
            stroke="rgb(180, 100, 255)"
            strokeWidth={strokeWidth}
            rx={borderRadius}
            pathLength={perimeter}
            strokeDasharray={perimeter}
            strokeDashoffset={perimeter - progress * perimeter}
            filter="url(#glow)"
          />
        </svg>

        {/* Small dot at burning point */}
        <AnimatePresence>
          {isActive && !showExplosion && (
            <motion.div
              className="absolute rounded-full z-50"
              style={{
                width: 16,
                height: 16,
                left: fusePosition.x - 8,
                top: fusePosition.y - 8,
                background: "rgb(180, 100, 255)",
                boxShadow:
                  "0 0 12px hsl(280, 100%, 70%), 0 0 24px hsl(260, 100%, 60%)",
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [1, 0.8, 1],
              }}
              transition={{
                duration: 0.3,
                repeat: Infinity,
              }}
            />
          )}
        </AnimatePresence>

        {/* Timer - only show when > 0 */}
        {timeRemaining > 0 && !showExplosion && (
          <div className="absolute inset-0 flex items-center justify-center z-500">
            <motion.div
              className="text-2xl font-bold text-purple-400 tabular-nums overflow-visible p-20"
              style={{
                textShadow:
                  "0 0 20px hsl(280, 100%, 60%), 0 0 40px hsl(260, 80%, 50%)",
              }}
              animate={{
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
              }}
            >
              Hades thinking...
            </motion.div>
          </div>
        )}

        {/* Explosion */}
        <AnimatePresence>{showExplosion && <Explosion />}</AnimatePresence>

        <div className="flex flex-col gap-8 items-center justify-center h-[500px]">
          {/* Karma Text */}
          <KarmaText show={showKarmaText} />

          {/* Winners */}
          <AnimatePresence>
            {showWinners && (
              <motion.div
                className="inset-0 flex flex-col items-center gap-4 justify-center z-60 overflow-visible"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {winners.map((winner, index) => (
                  <motion.div
                    key={winner}
                    className="text-center overflow-visible mb-2"
                    initial={{ opacity: 0, scale: 0, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{
                      delay: index * 0.5,
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    }}
                  >
                    <motion.span
                      className="text-2xl font-black uppercase tracking-wider block"
                      style={{
                        background:
                          "linear-gradient(135deg, hsl(280, 100%, 80%) 0%, hsl(260, 100%, 65%) 50%, hsl(240, 80%, 55%) 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        filter:
                          "drop-shadow(0 0 15px hsl(270, 100%, 60%)) drop-shadow(0 0 30px hsl(250, 80%, 50%))",
                      }}
                    >
                      {winner}
                    </motion.span>
                    <motion.span
                      className="text-lg font-bold uppercase tracking-wide  flex items-center justify-center gap-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.5 + 0.2 }}
                      style={{
                        background:
                          "linear-gradient(135deg, hsl(200, 100%, 75%) 0%, hsl(280, 100%, 70%) 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        filter: "drop-shadow(0 0 10px hsl(260, 100%, 60%))",
                      }}
                    >
                      +{prize}
                      <img
                        src="/espont.png"
                        alt="espont"
                        className="inline-block h-5 w-5"
                      />
                    </motion.span>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
